import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  House,
  Phone,
  WhatsappLogo,
  Globe,
  EnvelopeSimple,
  UserPlus,
  Coins,
  UserCircle,
  Gift,
  Hourglass,
  Sparkle,
  SealCheck,
  Clock,
  Briefcase,
  SignOut,
  Heart,
} from "@phosphor-icons/react";
import { Logo, NEUTRAL_NAME, DEFAULT_CLAIM } from "../components/Logo";
import { NotificationBell } from "../components/NotificationBell";
import { PushToggle } from "../components/PushToggle";
import { ProgressTracker } from "../components/ProgressTracker";
import { CareerTracker } from "../components/CareerTracker";
import { BottomNav } from "../components/BottomNav";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_LABELS = {
  eingereicht: "Kontakt eingereicht",
  in_bearbeitung: "In Bearbeitung",
  termin: "Termin",
  kunde_geworden: "Kunde geworden",
};

const NAV = [
  { key: "uebersicht", label: "Übersicht", icon: House },
  { key: "empfehlen", label: "Empfehlen", icon: UserPlus },
  { key: "praemien", label: "Prämien", icon: Coins },
  { key: "profil", label: "Profil", icon: UserCircle },
];

const KARR_STATUS_LABELS = {
  eingegangen: "Eingegangen",
  kontaktiert: "Kontaktiert",
  gespraech: "Im Gespräch",
  gestartet: "Gestartet",
  kein_interesse: "Kein Interesse",
};

export default function KundeApp() {
  const { user, logout, refresh } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [auszahlungen, setAuszahlungen] = useState([]);
  const [karriere, setKarriere] = useState([]);
  const [tab, setTab] = useState("uebersicht");

  const load = useCallback(async () => {
    try {
      const [{ data: refs }, { data: pays }, { data: karr }] = await Promise.all([
        api.get("/referrals/mine"),
        api.get("/referrals/auszahlungen"),
        api.get("/karriere/mine"),
      ]);
      setReferrals(refs);
      setAuszahlungen(pays);
      setKarriere(karr);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const verfuegbar = referrals.filter((r) => r.praemie_freigeschaltet && !r.praemie_bezahlt).reduce((s, r) => s + r.praemie_betrag, 0);
  const ausgezahlt = referrals.filter((r) => r.praemie_bezahlt).reduce((s, r) => s + r.praemie_betrag, 0);
  const praemieAktiv =
    user?.berater_praemie_aktiv !== undefined
      ? user.berater_praemie_aktiv !== false
      : referrals.length === 0
        ? true
        : referrals.some((r) => r.praemie_aktiv !== false);
  const nav = praemieAktiv ? NAV : NAV.map((n) => (n.key === "praemien" ? { ...n, label: "Status" } : n));

  return (
    <div className="mx-auto min-h-screen max-w-md bg-[#F8F9FA]" data-testid="kunde-dashboard">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E2E8F0] bg-white/90 px-4 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] backdrop-blur-xl">
        <Logo {...(!praemieAktiv ? { name: NEUTRAL_NAME, claim: user?.berater_claim || DEFAULT_CLAIM } : { claim: user?.berater_claim || "" })} />
        <div className="flex items-center gap-2">
          {user?.berater_telefon ? <a data-testid="kunde-call-berater" href={`tel:${String(user.berater_telefon).replace(/\s/g, "")}`} aria-label="Berater anrufen" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#22C55E] text-white active:scale-95"><Phone size={16} weight="fill" /></a> : null}
          {user?.berater_whatsapp ? <a data-testid="kunde-whatsapp-berater" href={`https://wa.me/${String(user.berater_whatsapp).replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white active:scale-95"><WhatsappLogo size={16} weight="fill" /></a> : null}
          {user?.berater_webseite ? <a data-testid="kunde-webseite-berater" href={user.berater_webseite} target="_blank" rel="noreferrer" aria-label="Webseite" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0A1128] text-white active:scale-95"><Globe size={16} weight="fill" /></a> : null}
          {user?.berater_email ? <a data-testid="kunde-mail-berater" href={`mailto:${user.berater_email}`} aria-label="Berater schreiben" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C5A059] text-[#0A1128] active:scale-95"><EnvelopeSimple size={16} weight="fill" /></a> : null}
          <NotificationBell />
        </div>
      </header>

      <main className="px-4 pb-28 pt-4">
        {tab === "uebersicht" && (
          <Uebersicht name={user?.email} praemieAktiv={praemieAktiv} verfuegbar={verfuegbar} ausgezahlt={ausgezahlt} referrals={referrals} karriere={karriere} goEmpfehlen={() => setTab("empfehlen")} user={user} refresh={refresh} />
        )}
        {tab === "empfehlen" && <ReferralForm onCreated={() => { load(); setTab("uebersicht"); }} branche={user?.berater_branche} />}
        {tab === "praemien" && <PraemienList referrals={referrals} praemieAktiv={praemieAktiv} verfuegbar={verfuegbar} auszahlungen={auszahlungen} onDone={load} refresh={refresh} user={user} goEmpfehlen={() => setTab("empfehlen")} />}
        {tab === "profil" && <Profil user={user} logout={logout} refresh={refresh} />}
      </main>

      <BottomNav items={nav} active={tab} onChange={setTab} />
    </div>
  );
}

const praemieLabelK = (r) => (r?.praemie_typ === "sache"
  ? ((r.praemie_sache || "Sachprämie") + (r.praemie_betrag ? ` (~${r.praemie_betrag} €)` : ""))
  : `${r?.praemie_betrag || 0} €`);

function Uebersicht({ name, praemieAktiv, verfuegbar, ausgezahlt, referrals, karriere = [], goEmpfehlen, user, refresh }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="tab-uebersicht">
      <h1 className="font-display text-4xl font-medium tracking-tight text-[#0A1128]">Hallo!</h1>
      <p className="mt-1 truncate text-sm text-[#718096]">{name}</p>

      {(praemieAktiv || referrals.some((r) => r.praemie_betrag > 0)) && (
      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="relative overflow-hidden rounded-2xl bg-[#0A1128] p-5 text-white">
          <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#C5A059]/25 blur-2xl" />
          <Coins size={26} weight="duotone" color="#C5A059" />
          <p className="mt-4 text-xs text-white/60">Verfügbar</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#C5A059]" data-testid="reward-earned">{verfuegbar} €</p>
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <Hourglass size={26} weight="duotone" color="#0A1128" />
          <p className="mt-4 text-xs text-[#718096]">Ausgezahlt</p>
          <p className="mt-1 text-4xl font-extrabold leading-none text-[#0A1128]">{ausgezahlt} €</p>
        </div>
      </div>
      )}
      {!praemieAktiv && <div className="mt-5"><DankeKarte /></div>}

      <h2 className="mb-3 mt-8 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Deine Empfehlungen</h2>
      {referrals.length === 0 ? (
        <Empty text="Noch keine Empfehlung. Leg gleich los!" onClick={goEmpfehlen} />
      ) : (
        <div className="space-y-3">
          {referrals.map((r) => (
            <div key={r.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-[0_4px_20px_rgba(10,17,40,0.05)]" data-testid="referral-card">
              <div className="mb-3 flex items-start justify-between">
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-[#0A1128]">{r.kontakt_name}</p>
                  <p className="truncate text-xs text-[#718096]">{r.kontakt_email}</p>
                </div>
                {r.praemie_freigeschaltet && (r.praemie_typ === "sache" || r.praemie_betrag > 0) && (
                  <span className="shrink-0 rounded-full bg-[#C5A059]/15 px-2.5 py-0.5 text-xs font-semibold text-[#8a6d2f]">{praemieLabelK(r)} frei</span>
                )}
              </div>
              <ProgressTracker status={r.status} />
              <div className="mt-2 flex items-center gap-1.5 text-xs">
                {r.bestaetigt ? (
                  <span className="flex items-center gap-1 font-semibold text-[#276749]"><SealCheck size={13} weight="fill" /> Bestätigt</span>
                ) : (
                  <span className="flex items-center gap-1 text-[#DD6B20]"><Clock size={13} /> Bestätigung ausstehend</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {karriere.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">
            <Briefcase size={14} weight="fill" /> Deine Job-Empfehlungen
          </h2>
          <div className="space-y-3" data-testid="kunde-karriere-list">
            {karriere.map((k) => {
              const started = k.status === "gestartet";
              return (
                <div key={k.id} className={`relative overflow-hidden rounded-2xl border p-4 shadow-[0_4px_20px_rgba(10,17,40,0.06)] ${started ? "border-[#276749]/30 bg-[#276749]/[0.06]" : "border-[#C5A059]/30 bg-[#FBF8F1]"}`} data-testid="karriere-card">
                  <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#C5A059]/20 blur-2xl" />
                  <div className="relative mb-3 flex items-center gap-3">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${started ? "bg-[#276749]" : "bg-[#0A1128]"}`}>
                      <Briefcase size={22} weight="duotone" color="#C5A059" />
                    </div>
                    <p className="min-w-0 flex-1 truncate text-base font-bold text-[#0A1128]">{k.name}</p>
                    {k.belohnt && <span className="shrink-0 rounded-full bg-[#C5A059] px-2.5 py-1 text-[11px] font-extrabold text-[#0A1128]" data-testid="karriere-praemie-badge">Prämie ✓</span>}
                  </div>
                  <div className="relative">
                    <CareerTracker status={k.status} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {user?.berater_karriere_aktiv && <CareerCard user={user} refresh={refresh} />}
    </motion.div>
  );
}

function CareerCard({ user, refresh }) {
  const [busy, setBusy] = useState(false);
  const [refBusy, setRefBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", kontakt: "", notiz: "" });
  const [showInfo, setShowInfo] = useState(false);
  const done = user?.karriere_interesse;

  const expressSelf = async () => {
    setBusy(true);
    try {
      await api.post("/karriere-interesse");
      await refresh();
      toast.success("Danke für dein Interesse – dein Berater meldet sich bei dir.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitRef = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.kontakt.trim()) {
      toast.error("Bitte Name und Kontakt angeben.");
      return;
    }
    setRefBusy(true);
    try {
      await api.post("/karriere-empfehlung", form);
      toast.success("Danke! Dein Berater meldet sich bei der Person.");
      setForm({ name: "", kontakt: "", notiz: "" });
      setShowForm(false);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setRefBusy(false);
    }
  };

  const inputCls = "h-11 w-full rounded-lg border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/40 focus:border-[#C5A059] focus:outline-none";

  return (
    <div className="relative mt-8 overflow-hidden rounded-2xl bg-[#0A1128] p-5" data-testid="career-card">
      <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#C5A059]/20 blur-2xl" />
      <p className="relative text-[15px] leading-relaxed text-white/90">
        Du kennst viele Menschen? Vielleicht ist die {user?.berater_branche_zusatz || "Vermögensberatung"} – auch nebenberuflich – genau dein Weg.
      </p>
      <div className="relative mt-3">
        <button data-testid="career-info-toggle" onClick={() => setShowInfo((v) => !v)} className="flex items-center gap-1.5 text-xs font-semibold text-[#C5A059]">
          <Sparkle size={13} weight="fill" /> Was macht den Job aus?
        </button>
        {showInfo && (
          <ul className="mt-2 space-y-1.5 rounded-xl bg-white/5 p-3" data-testid="career-info">
            {[
              "Flexibel & nebenberuflich möglich",
              "Menschen zu finanzieller Freiheit begleiten",
              "Freie Zeiteinteilung, ortsunabhängig",
              "Attraktive Verdienstmöglichkeiten",
              "Fundierte Ausbildung & persönliche Begleitung",
            ].map((p) => (
              <li key={p} className="flex gap-2 text-[13px] leading-snug text-white/80">
                <SealCheck size={15} weight="fill" className="mt-0.5 shrink-0 text-[#C5A059]" /> {p}
              </li>
            ))}
          </ul>
        )}
      </div>
      {done ? (
        <p className="relative mt-3 text-sm font-medium text-[#C5A059]">Danke für dein Interesse – dein Berater meldet sich bei dir.</p>
      ) : (
        <button
          data-testid="career-interest-btn"
          onClick={expressSelf}
          disabled={busy}
          className="relative mt-4 inline-flex h-10 items-center gap-1.5 rounded-full bg-[#C5A059] px-5 text-sm font-semibold text-[#0A1128] transition-all active:scale-[0.98] disabled:opacity-60"
        >
          <Sparkle size={16} weight="fill" /> {busy ? "…" : "Unverbindlich mehr erfahren"}
        </button>
      )}

      <div className="relative mt-4 border-t border-white/10 pt-4">
        {sent ? (
          <p className="text-sm text-white/70" data-testid="career-ref-done">Danke! Wir kümmern uns darum.</p>
        ) : !showForm ? (
          <button
            data-testid="career-refer-toggle"
            onClick={() => setShowForm(true)}
            className="text-left text-sm font-medium text-white/80 underline decoration-[#C5A059]/60 underline-offset-4 transition-colors hover:text-white"
          >
            Sie kennen jemanden, der sich beruflich verändern möchte?
          </button>
        ) : (
          <form onSubmit={submitRef} className="space-y-2.5" data-testid="career-refer-form">
            <input data-testid="career-ref-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} />
            <input data-testid="career-ref-kontakt" value={form.kontakt} onChange={(e) => setForm({ ...form, kontakt: e.target.value })} placeholder="E-Mail oder Telefon" className={inputCls} />
            <input data-testid="career-ref-notiz" value={form.notiz} onChange={(e) => setForm({ ...form, notiz: e.target.value })} placeholder="Kurze Notiz (optional)" className={inputCls} />
            <div className="flex gap-2 pt-1">
              <button type="submit" data-testid="career-ref-submit" disabled={refBusy} className="inline-flex h-10 flex-1 items-center justify-center rounded-full bg-[#C5A059] text-sm font-semibold text-[#0A1128] disabled:opacity-60">
                {refBusy ? "…" : "Empfehlung senden"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="inline-flex h-10 items-center justify-center rounded-full border border-white/20 px-4 text-sm text-white/70">
                Abbrechen
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function PraemienList({ referrals, praemieAktiv, verfuegbar, auszahlungen = [], onDone, refresh, user, goEmpfehlen }) {
  const [busy, setBusy] = useState(false);
  const hasBank = !!(user?.iban && user?.kontoinhaber);
  const openKundeBeleg = async (id) => {
    try {
      const res = await api.get(`/kunde/auszahlungen/${id}/beleg.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    }
  };
  const pct = Math.min(100, Math.round((verfuegbar / 100) * 100));
  const offeneAnfrage = auszahlungen.find((a) => a.status === "offen");
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; } };
  const requestPayout = async () => {
    setBusy(true);
    try {
      await api.post("/referrals/auszahlung-anfordern");
      toast.success("Auszahlung angefordert – dein Berater meldet sich.");
      onDone();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3" data-testid="tab-praemien">
      <h1 className="mb-1 text-3xl font-extrabold tracking-tight text-[#0A1128]">{praemieAktiv ? "Prämien" : "Meine Empfehlungen"}</h1>

      {!praemieAktiv && <DankeKarte />}

      {(praemieAktiv || referrals.some((r) => r.praemie_betrag > 0)) && (
      <div className="relative overflow-hidden rounded-2xl bg-[#0A1128] p-5 text-white">
        <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#C5A059]/20 blur-2xl" />
        <p className="relative text-xs text-white/60">Verfügbar</p>
        <p className="relative text-4xl font-extrabold text-[#C5A059]" data-testid="verfuegbar-betrag">{verfuegbar} €</p>
        <div className="relative mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-[#C5A059] transition-all" style={{ width: `${pct}%` }} />
        </div>
        {offeneAnfrage ? (
          <p className="relative mt-3 text-xs text-white/70">Auszahlung über {offeneAnfrage.betrag} € angefordert – wird bearbeitet.</p>
        ) : (
          <p className="relative mt-2 text-xs text-white/60">{verfuegbar >= 100 ? (hasBank ? "Bereit zur Auszahlung." : "Bitte zuerst Bankdaten unten hinterlegen.") : `Auszahlung ab 100 € – noch ${100 - verfuegbar} €.`}</p>
        )}
        <button data-testid="auszahlung-btn" onClick={requestPayout} disabled={busy || verfuegbar < 100 || !!offeneAnfrage || !hasBank} className="relative mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#C5A059] text-sm font-semibold text-[#0A1128] transition-all active:scale-[0.99] disabled:opacity-40">
          <Gift size={16} weight="fill" /> {busy ? "…" : offeneAnfrage ? "Angefordert" : "Auszahlung anfordern"}
        </button>
      </div>
      )}

      {(praemieAktiv || referrals.some((r) => r.praemie_betrag > 0)) && <BankdatenBlock user={user} onSaved={() => { refresh && refresh(); onDone && onDone(); }} />}

      {praemieAktiv && auszahlungen.length > 0 && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-[0_4px_20px_rgba(10,17,40,0.05)]" data-testid="kunde-auszahlung-verlauf">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Auszahlungen</p>
          <div className="space-y-2">
            {auszahlungen.map((a) => (
              <div key={a.id} className="rounded-xl border border-[#F1F1F1] p-2.5" data-testid="kunde-auszahlung-row">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#0A1128]">{a.betrag} €</p>
                    <p className="mt-0.5 text-[11px] text-[#718096]">{fmtDate(a.status === "ausgezahlt" && a.ausgezahlt_am ? a.ausgezahlt_am : a.erstellt_am)}</p>
                  </div>
                  {a.status === "ausgezahlt" ? (
                    <span className="shrink-0 rounded-full bg-[#276749]/10 px-2.5 py-1 text-[11px] font-semibold text-[#276749]" data-testid="kunde-payout-status">Ausgezahlt</span>
                  ) : a.status === "abgelehnt" ? (
                    <span className="shrink-0 rounded-full bg-[#C0392B]/10 px-2.5 py-1 text-[11px] font-semibold text-[#C0392B]" data-testid="kunde-payout-status">Abgelehnt</span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-[#C5A059]/15 px-2.5 py-1 text-[11px] font-semibold text-[#8a6d2f]" data-testid="kunde-payout-status">Angefordert</span>
                  )}
                </div>
                {a.status === "ausgezahlt" && (
                  <button data-testid="kunde-beleg-btn" onClick={() => openKundeBeleg(a.id)} className="mt-2 h-9 w-full rounded-lg border border-[#E2E8F0] text-xs font-semibold text-[#0A1128] active:scale-[0.99]">Beleg (PDF)</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {referrals.length === 0 && <Empty text={praemieAktiv ? "Noch keine Prämien. Empfiehl jemanden!" : "Noch keine Empfehlung. Leg gleich los!"} onClick={goEmpfehlen} />}
      {referrals.map((r) => (
        <div key={r.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-[0_4px_20px_rgba(10,17,40,0.05)]" data-testid="reward-card">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="truncate text-base font-bold text-[#0A1128]">{r.kontakt_name}</p>
              <p className="mt-0.5 text-xs text-[#718096]">{STATUS_LABELS[r.status]}</p>
            </div>
            <div className="shrink-0 text-right">
              {(r.praemie_typ === "sache" || r.praemie_betrag > 0) && <p className="text-xl font-extrabold text-[#0A1128]">{praemieLabelK(r)}</p>}
              {r.praemie_bezahlt ? (
                <span className="text-[11px] font-semibold text-[#276749]">{praemieAktiv ? "Ausgezahlt" : "Erledigt"}</span>
              ) : r.praemie_freigeschaltet ? (
                <span className="text-[11px] font-semibold text-[#8a6d2f]">{praemieAktiv ? "Verfügbar" : "Erfolgreich"}</span>
              ) : (
                <span className="text-[11px] text-[#A0AEC0]">Ausstehend</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </motion.div>
  );
}

function Profil({ user, logout, refresh }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="tab-profil">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0A1128]">Profil</h1>
        <p className="mt-1 truncate text-sm text-[#718096]">{user?.email}</p>
      </div>
      <BankdatenBlock user={user} onSaved={refresh} />
      <div className="flex items-center justify-between rounded-2xl border border-[#E2E8F0] bg-white p-4">
        <div>
          <p className="text-sm font-semibold text-[#0A1128]">Benachrichtigungen</p>
          <p className="text-xs text-[#718096]">Push auf diesem Gerät.</p>
        </div>
        <PushToggle />
      </div>
      <button
        data-testid="logout-btn"
        onClick={logout}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#E2E8F0] bg-white text-sm font-semibold text-[#718096] transition-colors hover:bg-[#F1F5F9]"
      >
        <SignOut size={18} /> Abmelden
      </button>
    </motion.div>
  );
}

function RedeemButton({ refId, onDone }) {
  const [busy, setBusy] = useState(false);
  const redeem = async () => {
    setBusy(true);
    try {
      await api.post(`/referrals/${refId}/redeem`);
      toast.success("Prämie eingelöst – dein Berater wurde informiert.");
      onDone();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid="redeem-btn"
      onClick={redeem}
      disabled={busy}
      className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#C5A059] text-sm font-semibold text-[#0A1128] shadow-[0_8px_30px_rgba(197,160,89,0.3)] transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
    >
      <Gift size={18} weight="fill" /> {busy ? "…" : "Prämie einlösen"}
    </button>
  );
}

function ReferralForm({ onCreated, branche }) {
  const isFinanz = branche === "finanz";
  const [form, setForm] = useState({ kontakt_name: "", kontakt_telefon: "", kontakt_email: "" });
  const [zustimmung, setZustimmung] = useState(false);
  const [nurTipp, setNurTipp] = useState(false);
  const [nichtVn, setNichtVn] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!zustimmung) return toast.error("Bitte bestätige die Zustimmung der empfohlenen Person.");
    if (isFinanz && (!nurTipp || !nichtVn)) return toast.error("Bitte bestätige die beiden Tippgeber-Hinweise.");
    setBusy(true);
    try {
      await api.post("/referrals", { ...form, zustimmung, nur_tipp: nurTipp, nicht_vn: nichtVn });
      toast.success("Empfehlung gesendet – dein Berater kümmert sich darum.");
      setForm({ kontakt_name: "", kontakt_telefon: "", kontakt_email: "" });
      setZustimmung(false);
      setNurTipp(false);
      setNichtVn(false);
      onCreated();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, type = "text", placeholder = "") => (
    <div>
      <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.1em] text-[#C5A059]">{label}</label>
      <Input
        data-testid={`input-${key}`}
        type={type}
        required
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="h-12 rounded-xl border-[#E2E8F0] bg-white text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]"
      />
    </div>
  );

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="space-y-4"
      data-testid="referral-form"
    >
      <h1 className="text-3xl font-extrabold tracking-tight text-[#0A1128]">Neue Empfehlung</h1>
      <div className="space-y-4 rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-[0_4px_20px_rgba(10,17,40,0.05)]">
        {field("kontakt_name", "Name des Kontakts", "text", "Max Mustermann")}
        {field("kontakt_telefon", "Telefon", "tel", "+49 …")}
        {field("kontakt_email", "E-Mail", "email", "kontakt@beispiel.de")}
        <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F8F9FA] p-4">
          <Checkbox
            data-testid="zustimmung-checkbox"
            checked={zustimmung}
            onCheckedChange={setZustimmung}
            className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]"
          />
          <span className="text-sm leading-relaxed text-[#4A5568]">Person bestätigt die Weitergabe per E-Mail (DSGVO-konform).</span>
        </label>
        {isFinanz && (
          <>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F8F9FA] p-4" data-testid="nur-tipp-label">
              <Checkbox data-testid="nur-tipp-checkbox" checked={nurTipp} onCheckedChange={setNurTipp} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm leading-relaxed text-[#4A5568]">Ich gebe nur einen Tipp und berate bzw. vermittle selbst nicht.</span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl bg-[#F8F9FA] p-4" data-testid="nicht-vn-label">
              <Checkbox data-testid="nicht-vn-checkbox" checked={nichtVn} onCheckedChange={setNichtVn} className="mt-0.5 h-5 w-5 border-[#C5A059] data-[state=checked]:border-[#C5A059] data-[state=checked]:bg-[#C5A059]" />
              <span className="text-sm leading-relaxed text-[#4A5568]">Der Tipp betrifft eine andere Person – ich bin nicht selbst Versicherungsnehmer des empfohlenen Vertrags (§ 48b VAG).</span>
            </label>
            <p className="px-1 text-[11px] leading-relaxed text-[#A0AEC0]">Es gilt die <button type="button" onClick={() => window.open('/tippgeber-vereinbarung', '_blank')} className="underline" data-testid="tippgeber-link">Tippgeber-Vereinbarung</button>.</p>
          </>
        )}
        <button
          data-testid="submit-referral-btn"
          type="submit"
          disabled={busy}
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0A1128] text-base font-semibold text-white transition-all duration-200 active:scale-[0.98] disabled:opacity-60"
        >
          <UserPlus size={20} /> {busy ? "Wird gesendet…" : "Empfehlung senden"}
        </button>
      </div>
    </motion.form>
  );
}

function BankdatenBlock({ user, onSaved }) {
  const [iban, setIban] = useState(user?.iban || "");
  const [inhaber, setInhaber] = useState(user?.kontoinhaber || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/kunde/bankdaten", { iban, kontoinhaber: inhaber });
      toast.success("Bankdaten gespeichert.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-[0_4px_20px_rgba(10,17,40,0.05)]" data-testid="bankdaten-block">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#C5A059]">Bankdaten für die Auszahlung</p>
      <p className="mb-3 text-xs leading-relaxed text-[#718096]">Deine Prämie wird per Überweisung ausgezahlt. Bitte hinterlege IBAN und Kontoinhaber.</p>
      <div className="space-y-2.5">
        <Input data-testid="input-kontoinhaber" value={inhaber} onChange={(e) => setInhaber(e.target.value)} placeholder="Kontoinhaber" className="h-11 rounded-xl border-[#E2E8F0] bg-white text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
        <Input data-testid="input-iban" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="DE.. IBAN" className="h-11 rounded-xl border-[#E2E8F0] bg-white text-base focus-visible:border-[#C5A059] focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
      </div>
      <button data-testid="save-bankdaten-btn" onClick={save} disabled={busy} className="mt-3 h-11 w-full rounded-full bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.99] disabled:opacity-50">{busy ? "…" : "Bankdaten speichern"}</button>
      <p className="mt-3 text-[11px] leading-relaxed text-[#A0AEC0]" data-testid="steuer-hinweis">Hinweis: Die Prämie ist eine steuerpflichtige Einnahme. Für die Versteuerung bist du selbst verantwortlich. Es gilt die <button type="button" onClick={() => window.open('/tippgeber-vereinbarung', '_blank')} className="underline">Tippgeber-Vereinbarung</button>.</p>
    </div>
  );
}

function DankeKarte() {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0A1128] p-5 text-white" data-testid="danke-karte">
      <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#C5A059]/20 blur-2xl" />
      <Heart size={26} weight="duotone" color="#C5A059" />
      <p className="relative mt-4 font-display text-2xl font-medium leading-tight" data-testid="danke-karte-title">Danke, dass du empfiehlst!</p>
      <p className="relative mt-2 text-sm leading-relaxed text-white/70">Eine Empfehlung ist das höchste Gut – der wahre Unternehmerlohn. Danke, dass du Menschen aus deinem Umfeld zu einer ehrlichen, guten Beratung verhilfst.</p>
    </div>
  );
}

function Empty({ text, onClick }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-[#E2E8F0] bg-white px-6 py-12 text-center">
      <Gift size={32} weight="duotone" color="#C5A059" />
      <p className="mt-3 text-sm text-[#718096]">{text}</p>
      <button onClick={onClick} className="mt-4 rounded-full bg-[#0A1128] px-6 py-2.5 text-sm font-semibold text-white">Jetzt empfehlen</button>
    </div>
  );
}